import type { IBusiness, IContact } from '@trii/types/dist/Contacts/contacts';
import { Section } from '../../../layout';
import { EntityInfoLabel } from '../../../shared';
import { useTranslationContext } from '../../../context/TranslationContext';
import { Typography } from '@mui/material';

export type AddressInformationProps = {
  entityType: 'contact' | 'business';
  entityData: IContact | IBusiness;
  baseUrl?: string;
  spaceId?: string;
  onEntityUpdated?: (entity: unknown) => void;
  onDirectionSearchClick?: () => void;
};

const AddressInformation = ({
  entityType,
  entityData,
  baseUrl,
  spaceId,
  onEntityUpdated,
  onDirectionSearchClick,
}: AddressInformationProps) => {
  const commonProps = {
    baseUrl,
    spaceId,
    entityId: String((entityData as any).id),
    onEntityUpdated,
  };
  const t = useTranslationContext();

  const AddressGroup = ({
    addressNumber,
    title,
  }: {
    addressNumber: 'address1' | 'address2';
    title: string;
  }) => (
    <div className="flex flex-col gap-4 rounded-xl p-4">
      <Typography variant="body2">{title}</Typography>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {/* Street takes full width */}
        <div className="col-span-2">
          <EntityInfoLabel
            entityType={entityType as any}
            title={t('conversations.contactDetails.street')}
            value={entityData[addressNumber]?.street ?? ''}
            {...commonProps}
            constructUpdateObject={(value: any) => ({
              [addressNumber]: { street: value as string },
            })}
            minWidth="100%"
          />
        </div>

        {/* City & State share a row */}
        <EntityInfoLabel
          entityType={entityType as any}
          title={t('conversations.contactDetails.city')}
          value={entityData[addressNumber]?.city ?? ''}
          {...commonProps}
          constructUpdateObject={(value: any) => ({
            [addressNumber]: { city: value as string },
          })}
          minWidth="100%"
        />
        <EntityInfoLabel
          entityType={entityType as any}
          title={t('conversations.contactDetails.state')}
          value={entityData[addressNumber]?.state ?? ''}
          {...commonProps}
          constructUpdateObject={(value: any) => ({
            [addressNumber]: { state: value as string },
          })}
          minWidth="100%"
        />

        {/* Postcode & Country share a row */}
        <EntityInfoLabel
          entityType={entityType as any}
          title={t('conversations.contactDetails.postcode')}
          value={entityData[addressNumber]?.zipcode ?? ''}
          {...commonProps}
          constructUpdateObject={(value: any) => ({
            [addressNumber]: { zipcode: value as string },
          })}
          minWidth="100%"
        />
        <EntityInfoLabel
          entityType={entityType as any}
          title={t('conversations.contactDetails.country')}
          value={entityData[addressNumber]?.country ?? ''}
          {...commonProps}
          constructUpdateObject={(value: any) => ({
            [addressNumber]: { country: value as string },
          })}
          minWidth="100%"
        />
      </div>
    </div>
  );

  return (
    <Section
      title={t('conversations.contactDetails.addressInformation')}
      enableDirectionSearch={true}
      onDirectionSearchClick={onDirectionSearchClick}
    >
      <div className="flex flex-col gap-4">
        <AddressGroup
          addressNumber="address1"
          title={t('conversations.contactDetails.primaryAddress')}
        />
        <AddressGroup
          addressNumber="address2"
          title={t('conversations.contactDetails.secondaryAddress')}
        />
      </div>
    </Section>
  );
};

export default AddressInformation;
